import { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api, { fmtErr, wsUrl } from "../lib/api";
import { toast } from "sonner";
import { ArrowLeft, Send, MessageSquare, Film } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { logSwallow } from "../lib/log";
import GifPicker from "../components/gifs/GifPicker";
import GifImage from "../components/gifs/GifImage";
import TranslatedText from "../components/app/TranslatedText";

export default function VibeDetail() {
  const { id } = useParams();
  useAuth(); // ensures redirect when logged out; no field currently needed here
  const { t } = useTranslation();
  const nav = useNavigate();
  const [vibe, setVibe] = useState(null);
  const [comments, setComments] = useState([]);
  const [isAuthor, setIsAuthor] = useState(false);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [replying, setReplying] = useState(null); // comment id being replied to
  const [replyText, setReplyText] = useState("");
  const [gifOpen, setGifOpen] = useState(false);
  const wsRef = useRef(null);

  const loadVibe = useCallback(async () => {
    // No dedicated GET single vibe endpoint; fetch from feed and find.
    try {
      const { data } = await api.get("/vibes/feed");
      const found = data.vibes.find(v => v.id === id);
      if (!found) { toast("Vibe expired or out of scope."); nav("/live-room"); return; }
      setVibe(found);
    } catch (e) { toast.error(fmtErr(e)); }
  }, [id, nav]);

  const loadComments = useCallback(async () => {
    try {
      const { data } = await api.get(`/vibes/${id}/comments`);
      setComments(data.comments); setIsAuthor(data.is_author);
    } catch (e) { logSwallow("vibe.loadComments", e); }
  }, [id]);

  useEffect(() => {
    loadVibe(); loadComments();
    // Realtime: refresh comments when author reacts / replies / new comment.
    // wsUrl() pulls the current token from authStore (no direct localStorage).
    let ws = null;
    try {
      const url = wsUrl("/user");
      if (url) {
        ws = new WebSocket(url);
        ws.onmessage = (ev) => {
          try {
            const m = JSON.parse(ev.data);
            if (["comment.reaction", "vibe.comment", "match.new"].includes(m.type) && (!m.vibe_id || m.vibe_id === id)) {
              loadComments();
            }
          } catch (e) { logSwallow("vibe.ws.parse", e); }
        };
        ws.onerror = (e) => logSwallow("vibe.ws.error", e);
        wsRef.current = ws;
      }
    } catch (e) { logSwallow("vibe.ws.open", e); }
    return () => {
      // Close only the connection this effect opened, then null the ref
      if (ws) { try { ws.close(); } catch (e) { logSwallow("vibe.ws.close", e); } }
      if (wsRef.current === ws) wsRef.current = null;
    };
  }, [id, loadVibe, loadComments]);

  const sendComment = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/vibes/${id}/comments`, { text: text.trim() });
      if (data.promoted_to_chat && data.match_id) {
        toast.success("Match exists — message sent to chat.");
        nav(`/chat/${data.match_id}`);
        return;
      }
      setText("");
      toast.success("Comment sent privately.");
      loadComments();
    } catch (e) { toast.error(fmtErr(e)); }
    finally { setBusy(false); }
  };

  const sendCommentGif = async (gif) => {
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/vibes/${id}/comments`, {
        content_type: "gif",
        text: "",
        gif: {
          gif_url: gif.gif_url,
          gif_preview_url: gif.gif_preview_url,
          gif_id: gif.gif_id,
          gif_slug: gif.gif_slug,
          gif_width: gif.gif_width,
          gif_height: gif.gif_height,
        },
      });
      if (data.promoted_to_chat && data.match_id) {
        toast.success("Match exists — GIF sent to chat.");
        nav(`/chat/${data.match_id}`);
        return;
      }
      toast.success("GIF sent privately.");
      loadComments();
    } catch (e) { toast.error(fmtErr(e)); }
    finally { setBusy(false); }
  };

  const reply = async (commentId) => {
    if (!replyText.trim()) return;
    try {
      const { data } = await api.post(`/vibes/${id}/comments/${commentId}/reply`, { body: replyText.trim() });
      toast.success("Chat opened.");
      nav(`/chat/${data.match_id}`);
    } catch (e) { toast.error(fmtErr(e)); }
  };

  if (!vibe) return <div className="min-h-screen bg-black text-zinc-500 flex items-center justify-center">…</div>;

  return (
    <div className="min-h-screen bg-black text-white relative">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(230,0,0,0.12)_0%,transparent_70%)] pointer-events-none" />
      <div className="relative max-w-md mx-auto px-5 pt-6 pb-24">
        <div className="flex items-center justify-between" data-testid="vibe-detail-header">
          <Link to="/live-room" className="flex items-center gap-2 text-zinc-300 hover:text-white" data-testid="vibe-detail-back">
            <ArrowLeft size={18}/> Back
          </Link>
          <span className="font-mono-accent text-zinc-400">VIBE</span>
          <span className="w-12" />
        </div>

        <div className="mt-6 rounded-3xl border border-white/5 bg-black/60 p-5" data-testid="vibe-card">
          <div className="text-[10px] font-mono-accent text-zinc-500">{vibe.user?.real_name || vibe.user?.display_name || "anonymous"}</div>
          <div className="mt-2 text-2xl font-bold leading-tight"><TranslatedText text={vibe.text} testId="vibe-detail-tx" /></div>
          {vibe.content_type === "gif" && vibe.gif_url && (
            <div className="mt-3">
              <GifImage src={vibe.gif_url} maxHeight={200} testId="vibe-detail-gif" />
            </div>
          )}
          {vibe.venue_label && (
            <div className="mt-2 text-zinc-500 text-[11px] font-mono-accent tracking-wider">{vibe.venue_label.toUpperCase()}</div>
          )}
        </div>

        {!isAuthor && (
          <form onSubmit={sendComment} className="mt-6 rounded-3xl border border-white/5 bg-black/60 p-4" data-testid="comment-form">
            <div className="text-zinc-300 text-sm mb-2 flex items-center gap-2">
              <MessageSquare size={14} className="text-[#E60000]" /> Send a private comment to {vibe.user?.real_name || vibe.user?.display_name || "them"}
            </div>
            <p className="text-zinc-500 text-xs mb-3">Only they see it. If they reply, a chat opens.</p>
            <textarea
              data-testid="comment-input"
              value={text}
              onChange={(e)=>setText(e.target.value.slice(0,240))}
              placeholder={t("vibe.commentPlaceholder")}
              rows={2}
              className="w-full bg-black/50 border border-white/10 rounded-2xl px-4 py-3 text-sm outline-none focus:border-[#E60000] resize-none"
            />
            <div className="flex justify-between items-center mt-2">
              <span className="text-zinc-600 text-xs font-mono-accent">{text.length}/240</span>
              <div className="flex items-center gap-2">
                <button type="button" onClick={()=>setGifOpen(true)} data-testid="comment-gif-btn" title="Add a GIF" className="px-3 py-2 rounded-full border border-white/10 text-zinc-300 hover:border-[#E60000]/40 hover:text-white transition flex items-center gap-1">
                  <Film size={14}/> <span className="text-[11px] font-mono-accent">{t("liveRoom.gif")}</span>
                </button>
                <button data-testid="comment-send" disabled={busy || !text.trim()} className="px-5 py-2 rounded-full bg-[#E60000] hover:brightness-110 disabled:opacity-40 font-bold text-sm transition flex items-center gap-2">
                  <Send size={14}/> {t("vibe.send")}
                </button>
              </div>
            </div>
            <GifPicker open={gifOpen} onClose={()=>setGifOpen(false)} onSelect={sendCommentGif} />
            {comments.length > 0 && (
              <div className="mt-4 pt-4 border-t border-white/5">
                <p className="text-zinc-500 text-xs font-mono-accent mb-2">YOUR COMMENT</p>
                {comments.map(c => {
                  const rx = c.reactions || {};
                  const hasRx = Object.keys(rx).length > 0;
                  return (
                    <div key={c.id} className="text-zinc-300 text-sm bg-black/40 rounded-xl p-3" data-testid={`my-comment-${c.id}`}>
                      <div>{c.text && <TranslatedText text={c.text} testId={`my-comment-tx-${c.id}`} />}</div>
                      {c.content_type === "gif" && c.gif_url && (
                        <div className="mt-2">
                          <GifImage src={c.gif_url} maxHeight={200} testId={`my-comment-gif-${c.id}`} />
                        </div>
                      )}
                      {hasRx && (
                        <div className="mt-2 flex gap-1.5 flex-wrap" data-testid={`my-comment-reactions-${c.id}`}>
                          {Object.entries(rx).map(([emoji, uids]) => (
                            <span key={emoji} className="px-2 py-0.5 rounded-full bg-[#E60000]/15 border border-[#E60000]/30 text-xs" data-testid={`my-comment-reaction-${c.id}-${emoji}`}>
                              {emoji} <span className="text-zinc-400">{(uids || []).length}</span>
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
                <p className="text-zinc-600 text-xs mt-2">Waiting for {vibe.user?.real_name || vibe.user?.display_name || "them"} to reply…</p>
              </div>
            )}
          </form>
        )}

        {isAuthor && (
          <div className="mt-6" data-testid="author-comments">
            <p className="font-mono-accent text-zinc-400 mb-3">PRIVATE COMMENTS · ONLY YOU SEE THESE</p>
            {comments.length === 0 && (
              <div className="rounded-3xl border border-white/5 bg-black/40 p-6 text-center text-zinc-500 text-sm">No comments yet.</div>
            )}
            <div className="space-y-3">
              {comments.map(c => <AuthorCommentCard key={c.id} c={c} vibeId={id} onChange={loadComments} replying={replying} setReplying={setReplying} replyText={replyText} setReplyText={setReplyText} reply={reply} />)}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const EMOJIS = ["❤️", "🔥", "😂", "👀"];

function AuthorCommentCard({ c, vibeId, onChange, replying, setReplying, replyText, setReplyText, reply }) {
  const react = async (emoji) => {
    try {
      await api.post(`/vibes/${vibeId}/comments/${c.id}/reaction`, { emoji });
      onChange();
    } catch (e) { toast.error(fmtErr(e)); }
  };
  const reactions = c.reactions || {};
  return (
    <div className="rounded-2xl border border-white/5 bg-black/60 p-4" data-testid={`comment-${c.id}`}>
      <div className="flex justify-between items-start gap-2">
        <div className="flex-1">
          <div className="text-[10px] font-mono-accent text-zinc-500">{c.from?.real_name || c.from?.display_name || "anonymous"}</div>
          <div className="text-white mt-1">{c.text && <TranslatedText text={c.text} testId={`comment-tx-${c.id}`} />}</div>
          {c.content_type === "gif" && c.gif_url && (
            <div className="mt-2">
              <GifImage src={c.gif_url} maxHeight={200} testId={`comment-gif-${c.id}`} />
            </div>
          )}
        </div>
        {c.status === "promoted" ? (
          <Link to={`/chat/${c.match_id}`} className="text-xs font-mono-accent text-[#E60000]">OPEN CHAT →</Link>
        ) : (
          <button
            data-testid={`reply-btn-${c.id}`}
            onClick={()=>setReplying(replying===c.id ? null : c.id)}
            className="text-xs font-mono-accent text-[#E60000] hover:text-white"
          >{replying===c.id ? "CANCEL" : "REPLY"}</button>
        )}
      </div>
      {/* Reaction chips */}
      <div className="mt-3 flex gap-1.5 flex-wrap items-center" data-testid={`reactions-${c.id}`}>
        {Object.entries(reactions).map(([emoji, uids]) => (
          <button
            key={emoji}
            onClick={() => react(emoji)}
            data-testid={`reaction-${c.id}-${emoji}`}
            className="px-2.5 py-1 rounded-full bg-[#E60000]/15 border border-[#E60000]/40 text-xs hover:bg-[#E60000]/25 transition"
          >
            {emoji} <span className="text-zinc-400">{(uids || []).length}</span>
          </button>
        ))}
        {/* Quick-add row */}
        <div className="flex gap-1 ml-1">
          {EMOJIS.filter(e => !reactions[e]).map(e => (
            <button
              key={e}
              onClick={() => react(e)}
              data-testid={`add-reaction-${c.id}-${e}`}
              className="w-7 h-7 rounded-full bg-white/5 hover:bg-white/10 text-sm transition"
            >{e}</button>
          ))}
        </div>
      </div>
      {replying === c.id && (
        <div className="mt-3 flex gap-2" data-testid={`reply-area-${c.id}`}>
          <input
            autoFocus
            value={replyText}
            onChange={(e)=>setReplyText(e.target.value)}
            placeholder="Your reply opens chat…"
            className="flex-1 bg-black/50 border border-white/10 rounded-full px-4 py-2 text-sm outline-none focus:border-[#E60000]"
            data-testid={`reply-input-${c.id}`}
          />
          <button
            onClick={()=>reply(c.id)}
            disabled={!replyText.trim()}
            data-testid={`reply-send-${c.id}`}
            className="px-4 py-2 rounded-full bg-[#E60000] hover:brightness-110 disabled:opacity-40 font-bold text-sm transition"
          >REPLY & OPEN CHAT</button>
        </div>
      )}
    </div>
  );
}
